import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Providers/AuthProvider';

const TACOS_TEMPLATE_ID = 'd0e1f2a3-b4c5-6d7e-8f9a-0b1c2d3e4f5a';

// Componente Drag & Drop Reutilizable con Botón de Eliminar
const FileDropzone = ({ label, value, onUpload, onDelete, type, fieldName, index = -1, uploadingField }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const isUploading = uploadingField === (fieldName + (index >= 0 ? `-${index}` : ''));

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            onUpload(file, fieldName, index, type);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onUpload(file, fieldName, index, type);
        }
    };

    return (
        <div className="mb-3">
            {label && <label className="form-label fw-bold small mb-1">{label}</label>}
            <div 
                className={`border rounded p-3 text-center position-relative transition-all d-flex flex-column align-items-center justify-content-center ${isDragOver ? 'border-primary bg-primary-subtle' : 'border-dashed border-secondary-subtle bg-light'}`}
                style={{ 
                    borderStyle: 'dashed', 
                    borderWidth: '2px', 
                    minHeight: '110px',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-${fieldName}-${index}`).click()}
            >
                <input 
                    type="file" 
                    id={`file-${fieldName}-${index}`} 
                    className="d-none" 
                    onChange={handleFileChange} 
                />
                
                {isUploading ? (
                    <div className="d-flex flex-column align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary mb-1" role="status"></div>
                        <span className="small text-muted">Subiendo...</span>
                    </div>
                ) : value ? (
                    <div className="position-relative d-flex flex-column align-items-center w-100" onClick={(e) => e.stopPropagation()}>
                        <img 
                            src={`${API_URL.replace('/api/', '')}${value}`} 
                            alt="Preview" 
                            className="img-thumbnail mb-1 shadow-sm" 
                            style={{ maxHeight: '70px', objectFit: 'contain' }} 
                        />
                        <div className="d-flex gap-2 align-items-center">
                            <span className="small text-truncate text-muted d-block" style={{ maxWidth: '140px' }}>
                                {value.split('/').pop()}
                            </span>
                            <button 
                                type="button" 
                                className="btn btn-sm btn-outline-danger border-0 p-0 px-1"
                                title="Eliminar imagen"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(fieldName, index);
                                }}
                            >
                                <i className="bi bi-trash3-fill"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex flex-column align-items-center text-secondary small">
                        <i className="bi bi-cloud-arrow-up-fill fs-4 text-primary mb-1"></i>
                        <span>Arrastra o haz clic</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const TacosThemeModal = ({ show, onClose, onSave }) => {
    const { authenticatedFetch } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const [themeName, setThemeName] = useState('');
    const [coverTitle, setCoverTitle] = useState('');
    const [coverLogo, setCoverLogo] = useState('');
    const [gameBackground, setGameBackground] = useState('');
    const [fanBackground, setFanBackground] = useState('');
    const [fanHeaderLogo, setFanHeaderLogo] = useState('');
    const [player1Name, setPlayer1Name] = useState('');
    const [player1Avatar, setPlayer1Avatar] = useState('');
    const [player2Name, setPlayer2Name] = useState('');
    const [player2Avatar, setPlayer2Avatar] = useState('');
    const [foodNameSingular, setFoodNameSingular] = useState('Taco');
    const [foodNamePlural, setFoodNamePlural] = useState('Tacos');
    const [floatingEmojis, setFloatingEmojis] = useState('🌮,🔥,✨,🌶️');
    const [selectPlayerMessage, setSelectPlayerMessage] = useState('');
    const [foodSteps, setFoodSteps] = useState(['', '', '', '']);
    const [plateSteps, setPlateSteps] = useState(['', '', '', '', '', '']);

    const [uploadingField, setUploadingField] = useState(null);

    const loadConfigs = async () => {
        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/gameconfigs/template/${TACOS_TEMPLATE_ID}`);
            if (res.ok) {
                const data = await res.json();
                setConfigs(data);
            }
        } catch (error) {
            console.error("Error loading configs", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            loadConfigs();
            handleNewConfig();
        }
    }, [show]);

    const handleSelectConfig = (config) => {
        setSelectedConfig(config);
        setIsEditing(true);

        setThemeName(config.nombreConfiguracion);
        
        let datos = {};
        if (typeof config.datosDinamica === 'string') {
            try { datos = JSON.parse(config.datosDinamica); } catch(e) {}
        } else {
            datos = config.datosDinamica || {};
        }

        setCoverTitle(datos.coverTitle || '');
        setCoverLogo(datos.coverLogo || '');
        setGameBackground(datos.gameBackground || '');
        setFanBackground(datos.fanBackground || '');
        setFanHeaderLogo(datos.fanHeaderLogo || '');
        setPlayer1Name(datos.player1Name || '');
        setPlayer1Avatar(datos.player1Avatar || '');
        setPlayer2Name(datos.player2Name || '');
        setPlayer2Avatar(datos.player2Avatar || '');
        setFoodNameSingular(datos.foodNameSingular || 'Taco');
        setFoodNamePlural(datos.foodNamePlural || 'Tacos');
        setFloatingEmojis(datos.floatingEmojis ? datos.floatingEmojis.join(',') : '🌮,🔥,✨,🌶️');
        setSelectPlayerMessage(datos.selectPlayerMessage || '');
        setFoodSteps(datos.foodSteps || ['', '', '', '']);
        setPlateSteps(datos.plateSteps || ['', '', '', '', '', '']);
    };

    const handleNewConfig = () => {
        setSelectedConfig(null);
        setIsEditing(true);

        setThemeName('');
        setCoverTitle('');
        setCoverLogo('');
        setGameBackground('');
        setFanBackground('');
        setFanHeaderLogo('');
        setPlayer1Name('');
        setPlayer1Avatar('');
        setPlayer2Name('');
        setPlayer2Avatar('');
        setFoodNameSingular('Taco');
        setFoodNamePlural('Tacos');
        setFloatingEmojis('🌮,🔥,✨,🌶️');
        setSelectPlayerMessage('');
        setFoodSteps(['', '', '', '']);
        setPlateSteps(['', '', '', '', '', '']);
    };

    const handleUploadImage = async (fileOrEvent, fieldName, index = -1, type = 'element') => {
        let file;
        if (fileOrEvent.target && fileOrEvent.target.files) {
            file = fileOrEvent.target.files[0];
        } else {
            file = fileOrEvent;
        }
        if (!file) return;

        setUploadingField(fieldName + (index >= 0 ? `-${index}` : ''));
        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('interaktia_token');
            const res = await fetch(`${API_URL}Tacos/UploadAsset?type=${type}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                const url = data.url;

                if (index >= 0) {
                    if (fieldName === 'foodSteps') {
                        const newSteps = [...foodSteps];
                        newSteps[index] = url;
                        setFoodSteps(newSteps);
                    } else if (fieldName === 'plateSteps') {
                        const newSteps = [...plateSteps];
                        newSteps[index] = url;
                        setPlateSteps(newSteps);
                    }
                } else {
                    if (fieldName === 'coverLogo') setCoverLogo(url);
                    if (fieldName === 'gameBackground') setGameBackground(url);
                    if (fieldName === 'fanBackground') setFanBackground(url);
                    if (fieldName === 'fanHeaderLogo') setFanHeaderLogo(url);
                    if (fieldName === 'player1Avatar') setPlayer1Avatar(url);
                    if (fieldName === 'player2Avatar') setPlayer2Avatar(url);
                }
            } else {
                alert("Error al subir la imagen");
            }
        } catch (error) {
            console.error("Error uploading image", error);
        } finally {
            setUploadingField(null);
        }
    };

    const handleDeleteImage = (fieldName, index = -1) => {
        if (index >= 0) {
            if (fieldName === 'foodSteps') {
                const newSteps = [...foodSteps];
                newSteps[index] = '';
                setFoodSteps(newSteps);
            } else if (fieldName === 'plateSteps') {
                const newSteps = [...plateSteps];
                newSteps[index] = '';
                setPlateSteps(newSteps);
            }
        } else {
            if (fieldName === 'coverLogo') setCoverLogo('');
            if (fieldName === 'gameBackground') setGameBackground('');
            if (fieldName === 'fanBackground') setFanBackground('');
            if (fieldName === 'fanHeaderLogo') setFanHeaderLogo('');
            if (fieldName === 'player1Avatar') setPlayer1Avatar('');
            if (fieldName === 'player2Avatar') setPlayer2Avatar('');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!themeName.trim()) {
            alert("El nombre del tema es obligatorio");
            return;
        }

        const dataPayload = {
            coverTitle,
            coverLogo,
            gameBackground,
            fanBackground,
            fanHeaderLogo,
            player1Name,
            player1Avatar,
            player2Name,
            player2Avatar,
            foodNameSingular,
            foodNamePlural,
            floatingEmojis: floatingEmojis.split(',').map(em => em.trim()).filter(Boolean),
            foodSteps,
            plateSteps,
            selectPlayerMessage
        };

        const configBody = {
            templateId: TACOS_TEMPLATE_ID,
            nombreConfiguracion: themeName.trim(),
            datosDinamica: dataPayload
        };

        setLoading(true);
        try {
            let res;
            if (selectedConfig) {
                // Update
                res = await authenticatedFetch(`${API_URL}v2/gameconfigs/${selectedConfig.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(configBody)
                });
            } else {
                // Create
                res = await authenticatedFetch(`${API_URL}v2/gameconfigs`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(configBody)
                });
            }

            if (res.ok) {
                alert("Tema guardado con éxito");
                setIsEditing(false);
                loadConfigs();
                if (onSave) onSave();
            } else {
                const errText = await res.text();
                alert(`Error al guardar: ${errText}`);
            }
        } catch (error) {
            console.error("Error saving theme", error);
            alert("Error de red al guardar");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar este tema?")) return;

        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/gameconfigs/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                alert("Tema eliminado con éxito");
                loadConfigs();
                if (onSave) onSave();
            } else {
                alert("No se pudo eliminar el tema");
            }
        } catch (error) {
            console.error("Error deleting theme", error);
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060, overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                    <div className="modal-header bg-dark text-white border-0">
                        <h5 className="modal-title fw-bold">🎨 Personalizar Temas (Tacos / Hamburguesas)</h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>
                    
                    <div className="modal-body p-4">
                        {!isEditing ? (
                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0">Temas Configuradas</h6>
                                    <button className="btn btn-primary btn-sm fw-bold" onClick={handleNewConfig}>
                                        <i className="bi bi-plus-lg me-1"></i> Nuevo Tema
                                    </button>
                                </div>

                                {loading && <div className="text-center my-3"><div className="spinner-border text-primary"></div></div>}

                                {!loading && configs.length === 0 && (
                                    <div className="alert alert-light text-center py-4 border">
                                        No hay temas personalizados creados. ¡Crea uno nuevo!
                                    </div>
                                )}

                                <div className="list-group">
                                    {configs.map(cfg => (
                                        <div key={cfg.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                                            <div>
                                                <span className="fw-bold text-dark">{cfg.nombreConfiguracion}</span>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-outline-secondary btn-sm" onClick={() => handleSelectConfig(cfg)}>
                                                    <i className="bi bi-pencil-fill"></i> Editar
                                                </button>
                                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(cfg.id)}>
                                                    <i className="bi bi-trash-fill"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSave}>
                                <div className="mb-4">
                                    <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => setIsEditing(false)}>
                                        <i className="bi bi-arrow-left me-1"></i> Volver al listado
                                    </button>
                                    <h6 className="fw-bold border-bottom pb-2">Configuración General del Tema</h6>
                                    
                                    <div className="row mb-3">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Nombre del Tema (para identificar en salas):</label>
                                            <input type="text" className="form-control" value={themeName} onChange={e => setThemeName(e.target.value)} placeholder="Ej. Lluvia de Hamburguesas" required />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Mensaje Selección Personaje (Celular):</label>
                                            <input type="text" className="form-control" value={selectPlayerMessage} onChange={e => setSelectPlayerMessage(e.target.value)} placeholder="Ej. Selecciona tu hamburguesería" />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="fw-bold border-bottom pb-2">1. Portada y Estilos de Fondo</h6>
                                    <div className="row mb-3">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Título de Portada:</label>
                                            <input type="text" className="form-control mb-3" value={coverTitle} onChange={e => setCoverTitle(e.target.value)} placeholder="Ej. ¡Lluvia de Hamburguesas!" />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <FileDropzone 
                                                label="Logo de Portada:" 
                                                value={coverLogo} 
                                                onUpload={handleUploadImage} 
                                                onDelete={handleDeleteImage} 
                                                type="logo" 
                                                fieldName="coverLogo" 
                                                uploadingField={uploadingField} 
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <FileDropzone 
                                                label="Fondo de Pantalla de Juego:" 
                                                value={gameBackground} 
                                                onUpload={handleUploadImage} 
                                                onDelete={handleDeleteImage} 
                                                type="bg" 
                                                fieldName="gameBackground" 
                                                uploadingField={uploadingField} 
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <FileDropzone 
                                                label="Fondo del Celular Aficionado:" 
                                                value={fanBackground} 
                                                onUpload={handleUploadImage} 
                                                onDelete={handleDeleteImage} 
                                                type="bg" 
                                                fieldName="fanBackground" 
                                                uploadingField={uploadingField} 
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <FileDropzone 
                                                label="Logo de Cabecera (Celular):" 
                                                value={fanHeaderLogo} 
                                                onUpload={handleUploadImage} 
                                                onDelete={handleDeleteImage} 
                                                type="logo" 
                                                fieldName="fanHeaderLogo" 
                                                uploadingField={uploadingField} 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="fw-bold border-bottom pb-2">2. Personajes (Avatares y Nombres)</h6>
                                    <div className="row">
                                        <div className="col-md-6 border-end">
                                            <p className="fw-bold text-danger mb-2">Jugador 1 (Rojo / Izquierda)</p>
                                            <div className="mb-3">
                                                <label className="form-label small fw-bold mb-1">Nombre:</label>
                                                <input type="text" className="form-control" value={player1Name} onChange={e => setPlayer1Name(e.target.value)} placeholder="Ej. Hamburguesa Roja" />
                                            </div>
                                            <FileDropzone 
                                                label="Avatar (Redimensionado 500x500px):" 
                                                value={player1Avatar} 
                                                onUpload={handleUploadImage} 
                                                onDelete={handleDeleteImage} 
                                                type="character" 
                                                fieldName="player1Avatar" 
                                                uploadingField={uploadingField} 
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <p className="fw-bold text-primary mb-2">Jugador 2 (Blanco / Derecha)</p>
                                            <div className="mb-3">
                                                <label className="form-label small fw-bold mb-1">Nombre:</label>
                                                <input type="text" className="form-control" value={player2Name} onChange={e => setPlayer2Name(e.target.value)} placeholder="Ej. Hamburguesa Blanca" />
                                            </div>
                                            <FileDropzone 
                                                label="Avatar (Redimensionado 500x500px):" 
                                                value={player2Avatar} 
                                                onUpload={handleUploadImage} 
                                                onDelete={handleDeleteImage} 
                                                type="character" 
                                                fieldName="player2Avatar" 
                                                uploadingField={uploadingField} 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="fw-bold border-bottom pb-2">3. Configuración de Elementos de Juego</h6>
                                    <div className="row mb-3">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Nombre Singular del Alimento:</label>
                                            <input type="text" className="form-control" value={foodNameSingular} onChange={e => setFoodNameSingular(e.target.value)} placeholder="Ej. Hamburguesa" />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Nombre Plural del Alimento:</label>
                                            <input type="text" className="form-control" value={foodNamePlural} onChange={e => setFoodNamePlural(e.target.value)} placeholder="Ej. Hamburguesas" />
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label fw-bold">Emojis Flotantes (Separados por coma):</label>
                                            <input type="text" className="form-control" value={floatingEmojis} onChange={e => setFloatingEmojis(e.target.value)} placeholder="Ej. 🍔,🔥,✨,🍟" />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="fw-bold border-bottom pb-2">4. Pasos del Alimento (Ingredientes en construcción - Max 500x500px)</h6>
                                    <div className="row">
                                        {foodSteps.map((step, idx) => (
                                            <div key={idx} className="col-md-3">
                                                <FileDropzone 
                                                    label={`Paso ${idx + 1}:`} 
                                                    value={step} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="step" 
                                                    fieldName="foodSteps" 
                                                    index={idx}
                                                    uploadingField={uploadingField} 
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="fw-bold border-bottom pb-2">5. Progreso del Plato (Max 500x500px)</h6>
                                    <div className="row">
                                        {plateSteps.map((plate, idx) => (
                                            <div key={idx} className="col-md-2">
                                                <FileDropzone 
                                                    label={`${idx} Alimento(s):`} 
                                                    value={plate} 
                                                    onUpload={handleUploadImage} 
                                                    onDelete={handleDeleteImage} 
                                                    type="plate" 
                                                    fieldName="plateSteps" 
                                                    index={idx}
                                                    uploadingField={uploadingField} 
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="modal-footer border-0 px-0 pb-0">
                                    <button type="button" className="btn btn-secondary" onClick={() => setIsEditing(false)}>Cancelar</button>
                                    <button type="submit" className="btn btn-success fw-bold" disabled={loading || uploadingField}>
                                        {loading ? 'Guardando...' : 'Guardar Tema'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export { TacosThemeModal };
